import { memo, ReactNode } from "react";
import styled from "styled-components";

import { Tooltip } from "@/components/ui/Tooltip";
import { TableHeaderCellProps } from "@/components/ui/table-final-saviour/Table/TableHeaderCell/TableHeaderCell";
import {
    TableHeaderCellSortableStyled,
    TableHeaderCellSortableContent,
    TableHeaderCellSortableSortIcon,
} from "@/components/ui/table-final-saviour/Table/TableHeaderCell/TableHeaderCellSortable/table-header-cell-sortable.styled-components";
import { SortOrder } from "@/components/ui/table-final-saviour/Table/TableHeaderCell/TableHeaderCellSortable/table-header-cell-sortable.types";
import { sortIconMap } from "@/components/ui/table-final-saviour/Table/TableHeaderCell/TableHeaderCellSortable/table-header-cell-sortable.utils";
import { Row } from "@/components/ui/table-final-saviour/Table/table.types";

const TooltipTriggerStyled = styled.span`
    display: flex;
    align-items: center;
    gap: 0.25rem;
    text-decoration: underline dotted;
`;

const TooltipStyled = styled(Tooltip)`
    z-index: 10;
`;

interface Props<T extends Row> extends TableHeaderCellProps<T> {
    tooltip: ReactNode;
    sortOrder: SortOrder;
    onClick: () => void;
}

const TableHeaderCellSortableWithTooltip = <T extends Row>(props: Props<T>) => {
    const { align, sortOrder } = props;
    const icon = sortIconMap[sortOrder];

    const justifyContentMap = {
        left: "flex-start",
        center: "center",
        right: "flex-end",
    };
    const justifyContent = align ? justifyContentMap[align as keyof typeof justifyContentMap] : undefined;

    return (
        <TableHeaderCellSortableStyled
            className={props.className}
            $align={props.align}
            $width={props.width}
            onClick={props.onClick}
        >
            <TableHeaderCellSortableContent $justifyContent={justifyContent}>
                <TooltipStyled content={props.tooltip} asChild>
                    <TooltipTriggerStyled>
                        {props.children}
                        <TableHeaderCellSortableSortIcon icon={icon} />
                    </TooltipTriggerStyled>
                </TooltipStyled>
            </TableHeaderCellSortableContent>
        </TableHeaderCellSortableStyled>
    );
};

const TableHeaderCellSortableWithTooltipMemo = memo(
    TableHeaderCellSortableWithTooltip,
) as typeof TableHeaderCellSortableWithTooltip;

export { TableHeaderCellSortableWithTooltipMemo as TableHeaderCellSortableWithTooltip };
export type { Props as TableHeaderCellSortableWithTooltipProps };
